import React from 'react'

import dbCodes from '../../server/dbCodes.js'
import {getColliTypeName} from '../../utils/getColliTypeName.js'
import {Card, Colors, H5, Icon, P, Row} from '../../src/components/UI/index.js'
import {colloStatuses} from '../utils/colloStatus.js'
import t from 'src/helpers/Languages.js'

const colloColumnStyles = {
    flexDirection: 'column',
    backgroundColor: Colors.backgroundWhite,
    border: `1px solid ${Colors.border}`,
    padding: '6px 6px'
}

// Whether the collo is not handled, and the text of its reason. For a pickup the reason is in collo.reason, for a
// delivery in collo.status.
function colloFailure(type, collo) {
    if (type === 'pickup') {
        return ['invalid', 'notready'].includes(collo.reason) ? dbCodes.colloReasons[collo.reason] : ''
    }

    return ['refused', 'missing'].includes(collo.status) ? dbCodes.colloStatus[collo.status] : ''
}

// The colli of a pickup or delivery address, unscanned colli first. For colli of multiple orders, isScanRequired and
// colloNumber give the scan requirement and the number shown per collo.
export function ColliList({type, colli, scanned, hasScanned, scanRequired, isScanRequired = () => scanRequired, colloNumber = (collo) => collo.index + 1, isScanView, reseller, onClickCollo, style = {}}) {
    return (
        <div style={{width: isScanView ? '95%' : '90%', margin: '0 auto', ...style}}>
            {[...colli].sort((a, b) => {
                if (scanned.includes(a.barcode) && scanned.includes(b.barcode)) {
                    return a.index - b.index
                } else if (scanned.includes(a.barcode)) {
                    return 1
                } else if (scanned.includes(b.barcode)) {
                    return -1
                }
            }).map((collo, sortedIndex) => {
                const failure = colloFailure(type, collo)

                return (
                    <Row
                        key={`collo${colloNumber(collo)}`}
                        style={{flex: 1, paddingLeft: 6, paddingRight: 6, ...colloColumnStyles, ...{
                            borderBottom: sortedIndex === colli.length - 1 ? `1px solid ${Colors.border}` : 'none'
                        }, minWidth: 'auto'}}
                        onClick={() => onClickCollo(collo)}
                    >
                        <Row style={{alignItems: 'center'}}>
                            <P style={{marginRight: 12, fontWeight: 700}}>{`${colloNumber(collo)}`}</P>

                            <P>{`${collo.barcode || t('noBarcode')}`}</P>

                            <Row style={{width: 'unset', marginLeft: 'auto', alignItems: 'center'}}>
                                {(scanned.includes(collo?.barcode)) && (
                                    <Icon style={{marginRight: 6}} size='md' icon='mdi mdi-barcode-scan' />
                                )}
                                <Icon
                                    style={{marginRight: 6, fontSize: 12, color: (() => {
                                        if (failure) {
                                            return Colors.colloStatusColor(null, collo).statusColor
                                        }

                                        if (scanned.includes(collo?.barcode) || collo.status === colloStatuses[type].ok) {
                                            return Colors.successBright
                                        }

                                        if (!isScanView) {
                                            if ((!collo?.barcode || !isScanRequired(collo)) && !hasScanned) {
                                                return Colors.successBright
                                            }
                                        }

                                        return Colors.white
                                    })()
                                    }}
                                    icon='mdi mdi-circle'
                                />
                            </Row>
                        </Row>

                        <Row>
                            <P style={{marginLeft: 22}}>{collo.type ? `${getColliTypeName(collo.type, reseller)}` : ''}</P>

                            {failure && (
                                <P style={{marginLeft: 'auto', width: '50%', textAlign: 'right'}}>{failure}{collo.reasonSpecification ? ` - ${collo.reasonSpecification}` : ''}</P>
                            )}
                        </Row>

                        {collo.description &&
                            <Row>
                                <Icon style={{marginRight: 6, marginTop: 2}} size='md' icon='mdi mdi-message-outline' />

                                <P style={{width: '65%'}}>{collo.description}</P>
                            </Row>
                        }
                    </Row>
                )
            })}
        </div>
    )
}

export function PackagingCard({packaging, index, onClickPackaging}) {
    return (
        <Card
            shadow='true'
            key={`packaging${index}`}
            onClick={() => onClickPackaging(index)}
            style={{marginTop: 3}}
        >
            <Row style={{alignItems: 'center'}}>
                <P style={{marginRight: 12, fontWeight: 700}}>{`${packaging.type || ''}`}</P>

                <P>{`${packaging.barcode || t('noBarcode')}`}</P>

                <Row style={{width: 'unset', marginLeft: 'auto', alignItems: 'center'}}>
                    {!packaging.status ?
                        '' :
                        packaging.status === 'collected' || packaging.status === 'delivered' ?
                            <Icon size='xs' style={{marginRight: 6, color: Colors.successBright}} icon='mdi mdi-circle' /> :
                            packaging.status === 'notcollected' ?
                                <Icon size='xs' style={{marginRight: 6, color: Colors.warningBright}} icon='mdi mdi-circle' /> :
                                <Icon size='xs' style={{marginRight: 6, color: Colors.errorBright}} icon='mdi mdi-circle' />
                    }
                </Row>
            </Row>
        </Card>
    )
}

// The emballage of an address. Emballage of other orders at this address (e.g. a Px to collect) is listed separately.
export function PackagesList({packages, packagingIds, isScanView, onClickPackaging, style = {}}) {
    const otherPackages = packages.filter((packaging) => !packagingIds.includes(packaging._id))

    return (
        <div style={{width: isScanView ? '95%' : '90%', margin: '0 auto', ...style}}>
            {packages.map((packaging, index) => {
                if (packagingIds.includes(packaging._id)) {
                    return (
                        <PackagingCard
                            key={`package${index}`}
                            packaging={packaging}
                            index={index}
                            onClickPackaging={onClickPackaging}
                        />
                    )
                }
            })}

            {otherPackages.length > 0 &&
                <>
                    <Row style={{alignItems: 'center', width: '90%', marginTop: 36, marginBottom: 12}}>
                        {packages.some((packaging) => !packaging.status) ?
                            <Icon style={{marginRight: 6}} size='md' icon='mdi mdi-alert-circle' /> :
                            <Icon size='md' style={{marginRight: 6, color: Colors.successBright}} icon='mdi mdi-check-circle' />
                        }

                        <H5 style={{marginBottom: 0}}>{t('collectPackaging')}</H5>
                    </Row>

                    {packages.map((packaging, index) => {
                        if (!packagingIds.includes(packaging._id)) {
                            return (
                                <PackagingCard
                                    key={`package${index}`}
                                    packaging={packaging}
                                    index={index}
                                    onClickPackaging={onClickPackaging}
                                />
                            )
                        }
                    })}
                </>
            }
        </div>
    )
}
